// src/components/registered-landing/RegisteredLandingPage.js
'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

import Navbar from '../../Navbar';
import Footer from '../../footer/Footer';
import FeaturedCourses from '../../landing/FeaturedCourses';

import RegisteredHeroText from './RegisteredHeroText';
import HeroRightCard from './HeroRightCard';
import ESASection from './ESASection';
import LearnerModal from './LearnerModal';
import TutorLandingPage from '../tutor/TutorLandingPage';

import styles from '../shared/styles/RegisteredLandingLayout.module.css';

export default function RegisteredLandingPage({ user }) {
  const router = useRouter();

  /* ==========================
     ROLE DETECTION
  ========================== */

  // Match Navbar logic: teacher is role === 'TEACHER'
  const isTutor = user?.role === 'TEACHER';

  const firstName =
    user?.firstName ||
    (user?.name ? user.name.split(' ')[0] : null) ||
    'there';

  // Tutor flow
  if (isTutor) {
    return <TutorLandingPage user={user} firstName={firstName} />;
  }

  /* ==========================
     PARENT / LEARNER STATE
  ========================== */

  const [learners, setLearners] = useState(user?.learners || []);
  const hasLearners = learners.length > 0;

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentLearner, setCurrentLearner] = useState(null);

  const [learnerName, setLearnerName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [learnerDescription, setLearnerDescription] = useState('');

  const years = Array.from(
    { length: 25 },
    (_, i) => new Date().getFullYear() - i
  );

  useEffect(() => {
    if (user?.learners && Array.isArray(user.learners)) {
      setLearners(user.learners);
    }
  }, [user]);

  /* ==========================
     LEARNER MODAL HELPERS
  ========================== */

  function openLearnerModal(learner = null) {
    setCurrentLearner(learner);
    setLearnerName(learner?.name || '');
    setAvatarUrl(learner?.avatarUrl || '');
    setLearnerDescription(learner?.description || '');

    if (learner?.birthday) {
      const d = new Date(learner.birthday);
      setBirthYear(d.getFullYear().toString());
      setBirthMonth((d.getMonth() + 1).toString());
      setBirthDay(d.getDate().toString());
    } else {
      setBirthYear('');
      setBirthMonth('');
      setBirthDay('');
    }

    setShowEditModal(true);
  }

  function closeLearnerModal() {
    setShowEditModal(false);
    setCurrentLearner(null);
    setLearnerName('');
    setAvatarUrl('');
    setLearnerDescription('');
    setBirthYear('');
    setBirthMonth('');
    setBirthDay('');
  }

  async function handleSaveLearner() {
    const nameVal = learnerName.trim();
    if (!nameVal) {
      alert('Please enter a learner name.');
      return;
    }

    const payload = {
      name: nameVal,
      avatarUrl,
      description: learnerDescription,
    };

    if (!currentLearner) {
      payload.birthday = `${birthYear || '2000'}-${birthMonth || '01'}-${birthDay || '01'}`;

      try {
        const response = await fetch('/api/learners', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(await response.text());
        }

        const newLearner = await response.json();
        setLearners((prev) => [...prev, newLearner]);
      } catch (error) {
        console.error(error);
        alert('Failed to create learner.');
        return;
      }
    } else {
      try {
        const response = await fetch(`/api/student-page/${currentLearner.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(await response.text());
        }

        const updatedLearner = await response.json();
        setLearners((prev) =>
          prev.map((l) => (l.id === currentLearner.id ? updatedLearner : l))
        );
      } catch (error) {
        console.error(error);
        alert('Failed to update learner.');
        return;
      }
    }

    closeLearnerModal();
  }

  async function handleDeleteLearner() {
    if (!currentLearner) return;

    const ok = confirm(
      `Delete learner "${currentLearner.name}" from your account?`
    );
    if (!ok) return;

    try {
      const res = await fetch(`/api/student-page/${currentLearner.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error(await res.text());
      setLearners((prev) => prev.filter((l) => l.id !== currentLearner.id));
      closeLearnerModal();
    } catch (err) {
      console.error(err);
      alert('Failed to delete learner.');
    }
  }

  /* ==========================
     CTA HANDLERS
  ========================== */

  const handleAddLearnerClick = () => openLearnerModal();
  const handleBrowseClasses = () => router.push('/catalog');
  const handleMyClasses = () => router.push('/my-classes');

  /* ==========================
     RENDER
  ========================== */

  return (
    <>
      <Navbar />

      <main className={styles.container}>
        <section className={styles.hero}>
          <RegisteredHeroText
            firstName={firstName}
            hasLearners={hasLearners}
            learners={learners}
            router={router}
            onAddLearner={handleAddLearnerClick}
            onBrowseClasses={handleBrowseClasses}
            onMyClasses={handleMyClasses}
          />

          <div className={styles.heroMedia}>
            <HeroRightCard
              hasLearners={hasLearners}
              learners={learners}
              router={router}
              onAddLearner={handleAddLearnerClick}
            />
          </div>
        </section>

        <section className={styles.recommendedSection}>
          <FeaturedCourses limit={6} />
        </section>

        <ESASection />
      </main>

      <Footer />

      {showEditModal && (
        <LearnerModal
          years={years}
          learnerName={learnerName}
          setLearnerName={setLearnerName}
          avatarUrl={avatarUrl}
          setAvatarUrl={setAvatarUrl}
          birthYear={birthYear}
          setBirthYear={setBirthYear}
          birthMonth={birthMonth}
          setBirthMonth={setBirthMonth}
          birthDay={birthDay}
          setBirthDay={setBirthDay}
          learnerDescription={learnerDescription}
          setLearnerDescription={setLearnerDescription}
          currentLearner={currentLearner}
          onClose={closeLearnerModal}
          onSave={handleSaveLearner}
          onDelete={handleDeleteLearner}
        />
      )}
    </>
  );
}